/**
 * Google reCAPTCHA v3 for the public forms. The script is loaded lazily on first interaction with a form (not on
 * page load). The token is verified on the server (action, hostname, age, score, single use); the client never
 * decides whether a request is accepted.
 */
interface Grecaptcha {
  ready(cb: () => void): void;
  execute(siteKey: string, options: { action: string }): Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

let loading: Promise<Grecaptcha> | null = null;

export function loadRecaptcha(siteKey: string): Promise<Grecaptcha> {
  if (typeof window === 'undefined') return Promise.reject(new Error('ssr'));
  if (loading) return loading;
  loading = new Promise<Grecaptcha>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.onload = () => (window.grecaptcha ? window.grecaptcha.ready(() => resolve(window.grecaptcha!)) : reject(new Error('recaptcha')));
    script.onerror = () => {
      loading = null;
      reject(new Error('recaptcha'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/** Returns a fresh token, or null when reCAPTCHA is not configured (the server then decides whether that is allowed). */
export async function recaptchaToken(siteKey: string | null | undefined, action: string): Promise<string | null> {
  if (!siteKey) return null;
  const g = await loadRecaptcha(siteKey);
  return g.execute(siteKey, { action });
}
